import type { ZoneDef, ZoneLayout } from '@jianghu/game-data';
import { createRng } from '@jianghu/shared';
import * as THREE from 'three';
import { bridgeArch, buildStructures, type AnimatedProp } from './structures';
import { detailTexture, groundTexture } from './textures';
import { buildGrass, buildTrees, windUniforms } from './vegetation';

export interface WorldVisuals {
  group: THREE.Group;
  sun: THREE.DirectionalLight;
  campfires: THREE.Vector3[];
  /** Visual ground height (bridge arch); gameplay remains on the flat plane. */
  groundHeight(x: number, z: number): number;
  update(t: number, dt: number, focus: THREE.Vector3): void;
  dispose(): void;
}

const EXTENT = 150;

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

function buildGround(zone: ZoneDef, texSize: number): THREE.Mesh {
  const seg = 150;
  const geo = new THREE.PlaneGeometry(EXTENT * 2, EXTENT * 2, seg, seg);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.getAttribute('position');
  const b = zone.bounds;
  const rng = createRng(zone.seed + 3);
  const bumps = Array.from(
    { length: 24 },
    () => [rng.range(-1, 1), rng.range(-1, 1), rng.range(0, 6)] as const,
  );
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const out = Math.max(x - b.maxX, b.minX - x, z - b.maxZ, b.minZ - z, 0);
    let h = smoothstep(2, 34, out) * (8 + Math.sin(x * 0.05 + 1.3) * 4 + Math.cos(z * 0.043) * 4);
    for (const [fx, fz, ph] of bumps)
      h += Math.sin(x * 0.07 * fx + z * 0.07 * fz + ph) * 0.6 * smoothstep(0, 20, out);
    // Carve the stream bed and the pond.
    for (const w of zone.water) {
      if (Math.abs(z - w.z) < w.hd + 0.4 && Math.abs(x - w.x) < w.hw + 4)
        h = Math.min(h, -0.55 * smoothstep(w.hd + 0.6, w.hd - 0.6, Math.abs(z - w.z)));
    }
    for (const st of zone.structures) {
      if (st.kind !== 'pond') continue;
      const d = Math.hypot(x - st.x, z - st.z);
      if (d < st.w / 2 + 0.6) h = Math.min(h, -0.55 * smoothstep(st.w / 2 + 0.6, st.w / 2 - 0.6, d));
    }
    pos.setY(i, h);
  }
  geo.computeVertexNormals();
  const tex = groundTexture(zone, EXTENT, texSize);
  const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.96, metalness: 0 });
  // Close-up detail: modulate the splat map with a tiling grain sampled in world space.
  const detail = detailTexture();
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uDetail = { value: detail };
    shader.vertexShader =
      'varying vec3 vGroundWorld;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\n vGroundWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      );
    shader.fragmentShader =
      'uniform sampler2D uDetail;\nvarying vec3 vGroundWorld;\n' +
      shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
       float dA = texture2D(uDetail, vGroundWorld.xz * 0.37).r;
       float dB = texture2D(uDetail, vGroundWorld.xz * 0.061 + 0.3).r;
       diffuseColor.rgb *= mix(0.78, 1.14, dA) * mix(0.9, 1.08, dB);`,
      );
  };
  mat.customProgramCacheKey = () => 'ground-detail';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.name = 'ground';
  return mesh;
}

function waterMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    fog: true,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      {
        uTime: { value: 0 },
        uDeep: { value: new THREE.Color('#1f4f55') },
        uShallow: { value: new THREE.Color('#4f8f88') },
        uSky: { value: new THREE.Color('#b9d4e6') },
        uSunDir: { value: new THREE.Vector3(0.5, 0.8, 0.3).normalize() },
      },
    ]),
    vertexShader: `
      varying vec3 vWorld;
      #include <fog_pars_vertex>
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorld = wp.xyz;
        vec4 mvPosition = viewMatrix * wp;
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: `
      uniform float uTime;
      uniform vec3 uDeep;
      uniform vec3 uShallow;
      uniform vec3 uSky;
      uniform vec3 uSunDir;
      varying vec3 vWorld;
      #include <fog_pars_fragment>
      void main() {
        vec2 p = vWorld.xz;
        float t = uTime;
        vec3 n = normalize(vec3(
          sin(p.x * 0.9 + t * 1.3) * 0.08 + sin(p.x * 2.3 - p.y * 1.7 + t * 2.1) * 0.05 + sin((p.x + p.y) * 4.1 + t * 3.2) * 0.02,
          1.0,
          cos(p.y * 1.1 - t * 1.1) * 0.08 + sin(p.y * 2.7 + p.x * 1.3 + t * 1.7) * 0.05));
        vec3 v = normalize(cameraPosition - vWorld);
        float fres = pow(1.0 - max(dot(n, v), 0.0), 3.0);
        vec3 col = mix(uDeep, uShallow, 0.35 + 0.25 * sin(p.x * 0.15 + t * 0.2));
        col = mix(col, uSky, fres * 0.65);
        vec3 h = normalize(uSunDir + v);
        float spec = pow(max(dot(n, h), 0.0), 120.0) * 1.6;
        float sparkle = step(0.985, fract(sin(dot(floor(p * 6.0), vec2(12.9898, 78.233)) + floor(t * 3.0)) * 43758.5453)) * 0.35;
        col += vec3(1.0, 0.95, 0.85) * (spec + sparkle * spec * 4.0);
        gl_FragColor = vec4(col, 0.86);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
  });
}

function buildMountains(zone: ZoneDef): THREE.Mesh {
  const rng = createRng(zone.seed + 17);
  const parts: THREE.BufferGeometry[] = [];
  const count = 54;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + rng.range(-0.05, 0.05);
    const r = rng.range(175, 250);
    const h = rng.range(35, 95);
    const rad = rng.range(30, 55);
    const g = new THREE.ConeGeometry(rad, h, 9, 4);
    g.translate(0, h / 2 - 2, 0);
    const p = g.getAttribute('position');
    const colors: number[] = [];
    const low = new THREE.Color('#3e5a46');
    const mid = new THREE.Color('#6a7268');
    const snow = new THREE.Color('#e8ecf0');
    const c = new THREE.Color();
    for (let k = 0; k < p.count; k++) {
      const y = p.getY(k);
      const jitter = 1 + (rng.next() - 0.5) * 0.25;
      if (y > 0.5 && y < h - 3)
        p.setXYZ(k, p.getX(k) * jitter, y + (rng.next() - 0.5) * 4, p.getZ(k) * jitter);
      const t = p.getY(k) / h;
      if (t > 0.72 && h > 60) c.copy(snow);
      else if (t > 0.35) c.copy(mid).lerp(low, (0.72 - t) * 0.6);
      else c.copy(low);
      colors.push(c.r, c.g, c.b);
    }
    g.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    g.rotateY(rng.range(0, Math.PI));
    g.translate(Math.sin(a) * r, 0, Math.cos(a) * r);
    parts.push(g.toNonIndexed());
    g.dispose();
  }
  const geo = mergeAll(parts);
  geo.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1 }),
  );
  mesh.name = 'mountains';
  return mesh;
}

function mergeAll(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  let total = 0;
  for (const p of parts) total += p.getAttribute('position').count;
  const pos = new Float32Array(total * 3);
  const col = new Float32Array(total * 3);
  let o = 0;
  for (const p of parts) {
    const pa = p.getAttribute('position').array as Float32Array;
    const ca = p.getAttribute('color').array as Float32Array;
    pos.set(pa, o * 3);
    col.set(ca, o * 3);
    o += p.getAttribute('position').count;
    p.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

function buildSky(top: string, horizon: string): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      uTop: { value: new THREE.Color(top) },
      uHorizon: { value: new THREE.Color(horizon) },
      uSunDir: { value: new THREE.Vector3(0.45, 0.55, 0.35).normalize() },
    },
    vertexShader: `varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: `
      uniform vec3 uTop; uniform vec3 uHorizon; uniform vec3 uSunDir; varying vec3 vDir;
      void main(){
        float h = clamp(vDir.y, 0.0, 1.0);
        vec3 col = mix(uHorizon, uTop, pow(h, 0.55));
        float sun = max(dot(normalize(vDir), uSunDir), 0.0);
        col += vec3(1.0, 0.85, 0.6) * (pow(sun, 220.0) * 2.5 + pow(sun, 8.0) * 0.18);
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(600, 32, 16), mat);
  mesh.name = 'sky';
  mesh.frustumCulled = false;
  mesh.renderOrder = -1;
  return mesh;
}

/** Builds every static visual of a zone from the same data the server simulates. */
export function buildWorld(
  zone: ZoneDef,
  layout: ZoneLayout,
  opts: { textureSize: number; grassDensity: number; shadowSize: number },
): WorldVisuals {
  const group = new THREE.Group();
  group.name = `zone:${zone.id}`;

  group.add(buildSky(zone.ambience.skyTop, zone.ambience.skyHorizon));
  group.add(buildGround(zone, opts.textureSize));
  group.add(buildMountains(zone));

  const water = waterMaterial();
  water.uniforms.uSky!.value = new THREE.Color(zone.ambience.skyHorizon).lerp(
    new THREE.Color(zone.ambience.skyTop),
    0.4,
  );
  for (const w of zone.water) {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w.hw * 2, w.hd * 2 + 0.8), water);
    m.rotation.x = -Math.PI / 2;
    m.position.set(w.x, -0.12, w.z);
    group.add(m);
  }
  for (const st of zone.structures) {
    if (st.kind !== 'pond') continue;
    const m = new THREE.Mesh(new THREE.CircleGeometry(st.w / 2 + 0.3, 32), water);
    m.rotation.x = -Math.PI / 2;
    m.position.set(st.x, -0.1, st.z);
    group.add(m);
  }

  const structures = buildStructures(zone);
  group.add(structures.group);
  group.add(buildTrees(layout.trees));
  if (opts.grassDensity > 0) group.add(buildGrass(zone, layout, opts.grassDensity));

  // Lighting: warm low sun + sky/ground bounce.
  const hemi = new THREE.HemisphereLight('#cfe0f2', '#5a4a32', 1.15);
  group.add(hemi);
  const sun = new THREE.DirectionalLight('#fff0d6', 2.7);
  sun.position.set(30, 45, 22);
  sun.castShadow = true;
  sun.shadow.mapSize.set(opts.shadowSize, opts.shadowSize);
  const sc = sun.shadow.camera;
  sc.left = -34;
  sc.right = 34;
  sc.top = 34;
  sc.bottom = -34;
  sc.near = 1;
  sc.far = 140;
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.03;
  group.add(sun, sun.target);

  // Flickering campfire lights.
  const fires = structures.campfires.map((p) => {
    const l = new THREE.PointLight('#ff8a3a', 14, 16, 2);
    l.position.copy(p).setY(1.2);
    group.add(l);
    return l;
  });
  const animated: AnimatedProp[] = structures.animated;
  const bridge = zone.structures.find((s) => s.kind === 'bridge');

  const sunOffset = new THREE.Vector3(30, 45, 22);
  return {
    group,
    sun,
    campfires: structures.campfires,
    groundHeight(x, z) {
      if (bridge && Math.abs(x - bridge.x) < bridge.w / 2 + 0.2 && Math.abs(z - bridge.z) < bridge.d / 2) {
        return bridgeArch(z - bridge.z, bridge.d, 0.65) + 0.05;
      }
      return 0;
    },
    update(t, dt, focus) {
      windUniforms.uTime.value = t;
      water.uniforms.uTime!.value = t;
      for (const a of animated) a.update(t, dt);
      fires.forEach((l, i) => {
        l.intensity = 12 + Math.sin(t * 13 + i) * 2.5 + Math.sin(t * 7.3 + i * 2) * 2;
      });
      // Keep the shadow frustum centred on the player.
      sun.position.copy(focus).add(sunOffset);
      sun.target.position.copy(focus);
    },
    dispose() {
      group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) {
          m.geometry.dispose();
          const mats = Array.isArray(m.material) ? m.material : [m.material];
          for (const mat of mats) {
            const map = (mat as THREE.MeshStandardMaterial).map;
            map?.dispose();
            mat.dispose();
          }
        }
      });
    },
  };
}
